import { Phone, ShieldCheck, Truck, BadgePoundSterling, Users, Sparkles } from "lucide-react";
import { PHONE_DISPLAY, PHONE_HREF, SERVICE_AREA } from "@/lib/contact";

const HERO_IMG =
    "https://customer-assets-agu9un31.emergentagent.net/job_skip-hire-sydney/artifacts/j2woq6yq_latest%20site%20background%20image.jpeg";

const trust = [
    { icon: Truck, label: "Same Day or Next Day Delivery" },
    { icon: Users, label: "Family Owned & Local" },
    { icon: ShieldCheck, label: "Fully Insured" },
    { icon: BadgePoundSterling, label: "Best Rates" },
    { icon: Sparkles, label: "Transparent Pricing" },
];

export const Hero = () => {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative overflow-hidden bg-tp-forest text-white"
        >
            {/* Background photo — real Tipping Point Skips truck */}
            <img
                src={HERO_IMG}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover opacity-90"
                style={{ objectPosition: "center 35%" }}
                loading="eager"
                decoding="async"
            />
            {/* Layered gradient overlay: darker on the left where copy sits,
                fading to lighter on the right so the branded skip bin is visible */}
            <div
                className="absolute inset-0 hidden lg:block"
                style={{
                    background:
                        "linear-gradient(90deg, rgba(11,58,31,0.94) 0%, rgba(11,58,31,0.85) 40%, rgba(11,58,31,0.35) 75%, rgba(11,58,31,0.15) 100%)",
                }}
            />
            {/* Mobile/tablet overlay: bottom fade + top vignette so hero text stays readable */}
            <div
                className="absolute inset-0 lg:hidden"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(11,58,31,0.85) 0%, rgba(11,58,31,0.75) 50%, rgba(11,58,31,0.90) 100%)",
                }}
            />
            {/* Grain */}
            <div className="absolute inset-0 tp-grain" />
            {/* Gold radial accent top-right */}
            <div
                className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(closest-side, rgba(232,185,35,0.35), rgba(232,185,35,0) 70%)",
                }}
            />

            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 md:pt-20 md:pb-24 lg:pt-24 lg:pb-28">
                <div className="grid gap-10 lg:grid-cols-12 items-center">
                    <div className="lg:col-span-7 tp-reveal">
                        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm px-3 py-1.5 text-xs uppercase tracking-[0.18em] text-tp-gold">
                            <span className="h-1.5 w-1.5 rounded-full bg-tp-recycle animate-pulse" />
                            Servicing {SERVICE_AREA}
                        </div>
                        <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                            <span className="text-white">Skip Bin Hire Across </span>
                            <span className="text-tp-gold">Sydney</span>
                            <span className="text-white">,</span>
                            <br className="hidden sm:block" />
                            <span className="text-white"> Same Day or </span>
                            <span className="text-tp-gold">Next Day</span>
                        </h1>
                        <p className="mt-5 text-base sm:text-lg text-white/90 max-w-2xl leading-relaxed">
                            Family owned, local and affordable. Bins from{" "}
                            <span className="font-semibold text-white">2 to 8 cubic metres</span>
                            {" "}delivered to your door — residential clean-ups, renovations and commercial jobs.
                        </p>

                        <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:gap-4">
                            <a
                                href={PHONE_HREF}
                                data-testid="hero-call-button"
                                className="tp-btn-primary text-base h-12 px-5 justify-center tp-focus"
                            >
                                <Phone size={18} strokeWidth={2.6} />
                                Call {PHONE_DISPLAY}
                            </a>
                            <a
                                href="#quote"
                                data-testid="hero-quote-button"
                                className="tp-btn-outline text-base h-12 px-5 justify-center tp-focus"
                            >
                                Get a Free Quote
                            </a>
                        </div>

                        <ul className="mt-8 flex flex-wrap gap-x-5 gap-y-2">
                            {trust.map((t) => (
                                <li
                                    key={t.label}
                                    className="flex items-center gap-2 text-xs sm:text-sm text-white/90"
                                >
                                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-tp-gold/15 ring-1 ring-tp-gold/40">
                                        <t.icon size={13} strokeWidth={2.6} className="text-tp-gold" />
                                    </span>
                                    {t.label}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Right card */}
                    <div className="lg:col-span-5 tp-reveal">
                        <div className="rounded-2xl bg-white/95 text-tp-ink backdrop-blur-sm p-5 md:p-7 shadow-[0_18px_50px_rgba(11,27,18,0.35)] border border-white/40">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-tp-forest font-semibold">
                                <span className="h-1.5 w-1.5 rounded-full bg-tp-recycle" />
                                Fastest response — call us
                            </div>
                            <h2 className="mt-2 font-heading text-2xl text-tp-forest leading-tight">
                                Speak with a real local
                            </h2>
                            <p className="mt-2 text-sm text-tp-ink/80">
                                Quotes over the phone in minutes. We&rsquo;ll help you pick the right
                                size and lock in a delivery window.
                            </p>
                            <a
                                href={PHONE_HREF}
                                data-testid="hero-card-call"
                                className="tp-btn-primary w-full justify-center mt-4 h-12 text-base tp-focus"
                            >
                                <Phone size={18} strokeWidth={2.6} />
                                Call {PHONE_DISPLAY}
                            </a>
                            <div className="mt-4 rounded-lg bg-tp-offwhite border-l-4 border-tp-gold px-3 py-3 text-xs text-tp-ink/80">
                                Prefer to write? Scroll down and fill in the quick quote form
                                &mdash; we&rsquo;ll get straight back to you.
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom decorative safety-stripe divider (kept subtle) */}
            <div className="relative h-3 tp-stripes opacity-90" />
        </section>
    );
};

export default Hero;
